// Paso 3 del asistente (PE-A3b): cálculos. Arma cada evaluación con las calculadoras de src/lib/pe a partir de
// las ENTRADAS que escribe el responsable y de los datos de los pasos 1 y 2 (que aquí son de solo lectura).
// Todo se recalcula en vivo; los totales nunca son dato fuente: en emergency_assessments se guardan las
// entradas, el resultado (con su firma) y las trazas.
//
// Evaluaciones y su fila en emergency_assessments (el CHECK de M33 permite estos métodos; Togawa va dentro de
// la fila 'rset' porque no hay método propio, y aforo y anchos van en 'aforo_egreso'):
//   meseri                 por sector o por grupo sin separación cortafuego (sector_id = primer sector del grupo)
//   aforo_egreso           plan (sector_id null)
//   rset                   plan: Togawa y RSET por separado (PE-23)
//   punto_encuentro        plan
//   verificacion           plan
//   amenaza_vulnerabilidad plan (una fila con todas las amenazas)

import { amenazaSismica, evaluarAmenaza } from './pe-amenazas';
import { calcularCargaFuego } from './pe-carga-fuego';
import { ESCALA_MESERI, FACTORES_X, FactorX, MedioY } from './pe-constantes-meseri';
import {
  EXTINTOR_DISTANCIA_NACIONAL, EXTINTOR_DISTANCIA_QUITO, FCA_RTQ5, NIVELES_RIESGO_RTQ3, NivelRiesgoRtq3, OcupacionFCA, RECORRIDO_NACIONAL, RECORRIDO_RTQ3,
} from './pe-constantes-normativa';
import { CONDICIONES_PUNTO_ENCUENTRO, Consecuencia, Nivel4, PELIGROS_PUNTO_ENCUENTRO, Probabilidad } from './pe-constantes-plan-referencia';
import type { MetodoEvaluacion, Ocupacion } from './pe-datos';
import { calcularAforo, Edificacion, rset, togawa, TOGAWA_POR_DEFECTO, verificarSalidas, SalidaMedida } from './pe-evacuacion';
import { calcularMeseri } from './pe-meseri';
import type { SectorEnPantalla } from './pe-paso2';
import { sectorAMotor } from './pe-paso2';
import { capacitacionBrigada, FilaBrigadaCapacitacion } from './pe-brigada';
import { evaluarPuntoEncuentro } from './pe-punto-encuentro';
import { doc, FUENTE_PUBLICA, Traza } from './pe-tipos';
import { plural } from '../plurales';
import { numEc, redondear } from './pe-unidades';
import { verificar } from './pe-verificacion';
import { criteriosDe, Respuesta, TipoAmenazaVulnerabilidad, vulnerabilidadPorAmenaza } from './pe-vulnerabilidad';
import type { ZonaSismica } from './pe-amenazas';
import { agruparSectores } from './pe-carga-fuego';

// ── Datos de los pasos 1 y 2 (solo lectura en el paso 3) ─────────────────────

export interface ContextoPaso3 {
  cantonCode: string | null;
  ocupacion: Ocupacion | null;
  areaM2: number | null;
  trabajadores: number | null;
  zona: ZonaSismica | null;
  zonaFuente: string | null;
  lugar: string | null;
  bomberosKm: number | null;
  bomberosMin: number | null;
  sectores: SectorEnPantalla[];       // con sector.id guardado
  brigadaMiembros: number | null;      // personas distintas en emergency_brigade_members (null = sin leer)
  brigada?: FilaBrigadaCapacitacion[] | null; // PE-38: capacitación de cada brigadista (emergency_brigade_members)
  edificacion?: Edificacion | null;    // emergency_plans.site_is_new (PE-36): Tabla 3 (nueva) o 4 (existente) de la RTQ 5
}

export const esQuitoCodigo = (c: ContextoPaso3) => c.cantonCode === '1701';

// ── Entradas que escribe el responsable (inputs de cada fila) ───────────────

export interface EntradaMeseriPaso3 {
  factores: Partial<Record<FactorX, string>>;
  medios: Partial<Record<MedioY, 'no' | 'sv' | 'cv'>>;
  brigada?: boolean;                   // sin dato: se toma de emergency_brigade_members
}

export interface EntradaAforoPaso3 {
  ocupacionFca?: OcupacionFCA | null;  // RTQ 5, Tabla 1; sin dato: se sugiere desde la ocupación del paso 1
  ocupantes?: number | null;           // obligatorio si la Tabla 1 no da FCA (almacenamiento)
  altoRiesgo?: boolean;
  salidas: SalidaMedida[];
}

export interface EntradaEvacuacionPaso3 {
  personas?: number | null;            // N; sin dato, el aforo o los trabajadores
  anchoM?: number | null;              // A
  distanciaM?: number | null;          // D
  k?: number | null;                   // editables (PE-17)
  v?: number | null;
  deteccionS?: number | null;
  alarmaS?: number | null;
  premovimientoS?: number | null;      // sin valor por defecto (PE-23)
}

type ClaveCondicion = typeof CONDICIONES_PUNTO_ENCUENTRO[number]['clave'];
type ClavePeligro = typeof PELIGROS_PUNTO_ENCUENTRO[number]['clave'];

export interface EntradaPuntoPaso3 {
  m2PorPersona?: number | null;
  areaDisponibleM2?: number | null;
  distanciaM?: number | null;
  distanciaMinimaM?: number | null;
  condiciones: Partial<Record<ClaveCondicion, boolean>>;
  peligros: Partial<Record<ClavePeligro, { p: Probabilidad; c: Consecuencia }>>;
}

export interface EntradaVerificacionPaso3 {
  distanciaMaxExtintorM?: number | null;
  iluminacion?: { duracionMin?: number | null; luxPromedio?: number | null; luxMinimo?: number | null; separacionMaxM?: number | null };
  fechaHidrostatica?: string | null;   // AAAA-MM-DD de la última prueba
  intervaloHidrostaticaAnos?: number | null; // editable (PE-32); por defecto 6
  // Por sector (PE-A3c, casos H2 y T10): clave = sector.id. Se guarda en las entradas de la verificación.
  porSector?: Record<string, EntradaSectorVerificacion>;
}

export interface EntradaSectorVerificacion {
  distanciaExtintorM?: number | null;
  recorridoM?: number | null;
  rociadores?: boolean | null;          // "¿Tiene rociadores automáticos?" (PE-A3c2)
  riesgoRtq3?: NivelRiesgoRtq3 | null;  // solo en Quito: nivel de riesgo de los contenidos (RTQ 3; definido en la RTQ 1)
}

export interface EntradaAmenazaPaso3 {
  id: string;
  tipo: TipoAmenazaVulnerabilidad;
  nombre: string;
  nivel?: Nivel4 | null;               // volcán, inundación y otra: lo elige el responsable
  fuente?: string | null;              // obligatoria salvo sismo (mapa del IGEPN o PDOT del GAD)
  respuestas: Record<string, Respuesta>;
  ajuste?: { nivel: Nivel4; justificacion: string } | null;
}

export interface EntradasPaso3 {
  meseri: Record<string, EntradaMeseriPaso3>;   // clave = unidad (sector o grupo)
  aforo: EntradaAforoPaso3;
  evacuacion: EntradaEvacuacionPaso3;
  punto: EntradaPuntoPaso3;
  verificacion: EntradaVerificacionPaso3;
  amenazas: EntradaAmenazaPaso3[];
}

export const entradasVacias = (): EntradasPaso3 => ({
  meseri: {}, aforo: { salidas: [] }, evacuacion: {}, punto: { condiciones: {}, peligros: {} }, verificacion: {},
  amenazas: [{ id: 'sismo', tipo: 'sismo', nombre: 'Sismos', respuestas: {} }],
});

// ── Resultado ────────────────────────────────────────────────────────────────

export interface AccionDerivada {
  sourceKey: string;          // clave única de origen (no se duplica al guardar otra vez)
  sourceMethod: MetodoEvaluacion;
  sectorId: string | null;
  norma: string;
  articulo: string;
  descripcion: string;
}

export interface Evaluacion {
  clave: string;              // id estable de la evaluación ('meseri:<sector>', 'verificacion', …)
  metodo: MetodoEvaluacion;
  sectorId: string | null;
  titulo: string;
  trazas: Traza[];
  resumen: Record<string, unknown>;
  acciones: AccionDerivada[];
}

const pv = (metodo: string, norma: string, articulo: string, nota: string, paso: number): Traza => ({
  metodo, norma, articulo_o_tabla: articulo, umbral: null, valor: null, unidad: '—', estado: 'por_validar', fuente_doc: doc.paso0('§4.1'), nota, paso,
});

const num = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v);

// Clave estable de una traza dentro de su evaluación: el artículo, y si se repite, el comienzo de la nota.
// F3a2: artículos cuyo texto se limpió (sin códigos internos). La clave de origen de las acciones ya guardadas usa el
// texto anterior: se mantiene igual para no perder el vínculo con ellas.
const CLAVE_ARTICULO_LEGADA: Record<string, string> = { 'Brigada capacitada': 'Brigada capacitada (PE-38)' };

export function claveTraza(t: Traza, todas: Traza[]): string {
  const mismas = todas.filter(x => x.articulo_o_tabla === t.articulo_o_tabla).length;
  const articulo = CLAVE_ARTICULO_LEGADA[t.articulo_o_tabla] ?? t.articulo_o_tabla;
  return mismas > 1 ? `${articulo}|${(t.nota ?? '').split(/[:(]/)[0].trim()}` : articulo;
}

function accionesDeNoCumple(ev: Omit<Evaluacion, 'acciones'>): AccionDerivada[] {
  return ev.trazas.filter(t => t.estado === 'no_cumple').map(t => ({
    sourceKey: `${ev.clave}|${claveTraza(t, ev.trazas)}`, sourceMethod: ev.metodo, sectorId: ev.sectorId,
    norma: t.norma, articulo: t.articulo_o_tabla,
    descripcion: `Corregir: ${t.articulo_o_tabla} (${t.norma}). Valor ${formatoValor(t.valor)} ${t.unidad !== '—' ? t.unidad : ''}`.trim()
      + `${t.umbral !== null ? `; umbral ${formatoValor(t.umbral)}` : ''}.${t.nota ? ` ${t.nota}` : ''}`,
  }));
}

function formatoValor(v: number | string | null): string {
  if (v === null) return '—';
  return typeof v === 'number' ? String(redondear(v, 2)).replace('.', ',') : v;
}

function cerrar(ev: Omit<Evaluacion, 'acciones'>, extra: AccionDerivada[] = []): Evaluacion {
  const trazas = ev.trazas.map(t => (t.estado === 'por_validar' && t.paso === undefined ? { ...t, paso: 3 } : t));
  const base = { ...ev, trazas };
  return { ...base, acciones: accionesDeNoCumple(base).concat(extra) };
}

// ── MESERI por unidad (sector o grupo sin separación cortafuego, PE-28b) ─────

export interface UnidadMeseri { clave: string; nombre: string; sectorId: string | null; sectores: SectorEnPantalla[] }

export function unidadesMeseri(sectores: SectorEnPantalla[]): UnidadMeseri[] {
  const out: UnidadMeseri[] = [];
  const sinSep = sectores.filter(s => !s.sector.fire_separated);
  const grupos: Record<string, SectorEnPantalla[]> = {};
  for (const s of sinSep) (grupos[s.sector.group_key?.trim() || '(sin separación cortafuego)'] ??= []).push(s);
  for (const s of sectores) {
    if (s.sector.fire_separated) out.push({ clave: `sector:${s.sector.id}`, nombre: s.sector.name, sectorId: s.sector.id ?? null, sectores: [s] });
  }
  for (const [g, ss] of Object.entries(grupos)) {
    if (ss.length === 1) out.push({ clave: `sector:${ss[0].sector.id}`, nombre: ss[0].sector.name, sectorId: ss[0].sector.id ?? null, sectores: ss });
    else out.push({ clave: `grupo:${g}`, nombre: ss.map(s => s.sector.name).join(' + '), sectorId: ss[0].sector.id ?? null, sectores: ss });
  }
  return out;
}

// Qs de la unidad: el del sector o el del grupo (áreas y materiales sumados, R mayor).
function qsUnidad(u: UnidadMeseri): { qs: number; origen: string } {
  const motor = u.sectores.map(sectorAMotor);
  // Todos los sectores de un grupo comparten la clave: agruparSectores devuelve uno solo, unido.
  const sector = motor.length === 1 ? motor[0] : agruparSectores(motor.map(s => ({ ...s, grupo: 'unidad' }))).sectores[0];
  const r = calcularCargaFuego(sector);
  return { qs: r.qsMJm2, origen: `Qs del capítulo 2 (${motor.length === 1 ? 'sector' : 'grupo sin separación cortafuego, R mayor'}: ${u.nombre})` };
}

export function evaluarMeseri(u: UnidadMeseri, e: EntradaMeseriPaso3 | undefined, c: ContextoPaso3): Evaluacion {
  const titulo = `MESERI · ${u.nombre}`;
  const base = { clave: `meseri:${u.clave}`, metodo: 'meseri' as const, sectorId: u.sectorId, titulo };
  const faltan: Traza[] = [];
  if (!num(c.bomberosKm) || !num(c.bomberosMin)) {
    faltan.push(pv('meseri', 'MESERI 1985', 'Factor distancia de los bomberos', 'Faltan la distancia (km) y el tiempo (min) a los bomberos', 1));
  }
  const factoresFaltan = (Object.keys(FACTORES_X) as FactorX[])
    .filter(f => f !== 'distancia_bomberos' && f !== 'carga_termica' && !e?.factores[f]);
  if (factoresFaltan.length) {
    faltan.push(pv('meseri', 'MESERI 1985', 'Factores X', `Faltan ${factoresFaltan.length} factores: ${factoresFaltan.map(f => FACTORES_X[f].etiqueta).join(', ')}`, 3));
  }
  let q: { qs: number; origen: string } | null = null;
  try { q = qsUnidad(u); } catch (err) {
    faltan.push(pv('meseri', 'RD 164/2025', 'Factor carga térmica', `No se puede calcular el Qs del capítulo 2: ${err instanceof Error ? err.message : err}`, 2));
  }
  if (faltan.length || !q) return cerrar({ ...base, trazas: faltan, resumen: {} });
  const brigada = e?.brigada ?? (c.brigadaMiembros !== null ? c.brigadaMiembros > 0 : false);
  const r = calcularMeseri({
    factores: e!.factores, medios: e!.medios, brigada, qsSectorMJm2: q.qs,
    distanciaBomberos: { km: c.bomberosKm as number, minutos: c.bomberosMin as number },
  });
  const trazas = r.trazas.map(t => (t.articulo_o_tabla === 'Factor carga térmica' ? { ...t, nota: `${t.nota}. ${q!.origen}` } : t));
  return cerrar({
    ...base, trazas,
    resumen: { x: r.x, y: r.y, b: r.b, p: r.p, aceptabilidad: r.aceptabilidad, cualitativa: r.cualitativa, qsMJm2: q.qs, desgloseX: r.desgloseX },
  });
}

// ── Aforo y anchos (RTQ 5; solo en el DMQ) ───────────────────────────────────

const FCA_DESDE_OCUPACION: Partial<Record<Ocupacion, OcupacionFCA>> = {
  industrial: 'industrial', oficinas: 'oficinas', almacenamiento: 'almacenamiento', residencial: 'residencial', alojamiento: 'alojamiento',
};

export const fcaSugerida = (o: Ocupacion | null): OcupacionFCA | null => (o ? FCA_DESDE_OCUPACION[o] ?? null : null);

export function evaluarAforo(e: EntradaAforoPaso3, c: ContextoPaso3): Evaluacion {
  const base = { clave: 'aforo_egreso', metodo: 'aforo_egreso' as const, sectorId: null, titulo: 'Aforo y anchos de egreso' };
  const aplicaRtq = esQuitoCodigo(c);
  const trazas: Traza[] = [];
  if (!aplicaRtq) {
    trazas.push({
      metodo: 'aforo', norma: 'RTQ 5/2026', articulo_o_tabla: 'Ámbito', umbral: null, valor: null, unidad: '—', estado: 'informativo',
      fuente_doc: doc.matriz('59-60'), nota: 'La RTQ 5 solo aplica en el Distrito Metropolitano de Quito (cantón 1701): los cálculos son de referencia',
    });
  }
  const fca = e.ocupacionFca ?? fcaSugerida(c.ocupacion);
  if (!num(c.areaM2)) return cerrar({ ...base, trazas: trazas.concat(pv('aforo', 'RTQ 5/2026', '5.1.1', 'Falta el área total', 1)), resumen: {} });
  if (!fca) return cerrar({ ...base, trazas: trazas.concat(pv('aforo', 'RTQ 5/2026', 'Tabla 1', 'Elige el uso de la Tabla 1 (m² por persona)', 3)), resumen: {} });
  const af = calcularAforo(c.areaM2, fca);
  let aforo = af.aforoPersonas;
  const tAforo = af.trazas.map(t => ({ ...t, estado: (aplicaRtq || t.estado === 'por_validar' ? t.estado : 'informativo') as Traza['estado'] }));
  if (aforo === null) {
    if (num(e.ocupantes)) {
      aforo = e.ocupantes;
      tAforo.splice(0, tAforo.length, { ...af.trazas[0], estado: 'informativo', valor: e.ocupantes, nota: `Ocupantes indicados por el responsable (la Tabla 1 no da FCA para ${fca})` });
    } else {
      tAforo.forEach(t => { t.paso = 3; });
    }
  }
  trazas.push(...tAforo);
  if (aforo === null) return cerrar({ ...base, trazas, resumen: { fca, aforo: null } });
  if (e.salidas.length === 0) {
    trazas.push(pv('ancho_egreso', 'RTQ 5/2026', '5.2', 'Faltan los anchos medidos de las salidas', 3));
    return cerrar({ ...base, trazas, resumen: { fca, fcaM2: FCA_RTQ5[fca].fca, aforo } });
  }
  const sectores = Object.fromEntries(c.sectores.filter(x => x.sector.id).map(x => [x.sector.id as string, { nombre: x.sector.name, ocupantes: x.sector.occupants ?? null }]));
  const s = verificarSalidas(aforo, e.salidas, { altoRiesgo: e.altoRiesgo, aplicaRtq, edificacion: c.edificacion ?? null, sectores });
  trazas.push(...s.trazas);
  return cerrar({ ...base, trazas, resumen: { fca, fcaM2: FCA_RTQ5[fca].fca, aforo, puertasMm: s.puertasMm, escalerasMm: s.escalerasMm, puertaRequeridaM: s.puertaRequeridaM, escaleraRequeridaM: s.escaleraRequeridaM } });
}

export function aforoDe(ev: Evaluacion): number | null {
  const a = ev.resumen.aforo;
  return typeof a === 'number' ? a : null;
}

// ── Togawa y RSET, por separado (PE-23) ─────────────────────────────────────

export function evaluarEvacuacion(e: EntradaEvacuacionPaso3, c: ContextoPaso3, aforo: number | null): Evaluacion {
  const base = { clave: 'rset', metodo: 'rset' as const, sectorId: null, titulo: 'Tiempo de salida (Togawa) y RSET' };
  const n = num(e.personas) ? e.personas : aforo ?? c.trabajadores;
  const faltan: string[] = [];
  if (!num(n)) faltan.push('personas (N)');
  if (!num(e.anchoM)) faltan.push('ancho de la salida (A)');
  if (!num(e.distanciaM)) faltan.push('distancia del recorrido (D)');
  if (faltan.length) return cerrar({ ...base, trazas: [pv('togawa', 'Togawa', 'TS = N/(A·K) + D/V', `Faltan: ${faltan.join(', ')}`, 3)], resumen: {} });
  const t = togawa({
    n: n as number, a: e.anchoM as number, d: e.distanciaM as number,
    k: num(e.k) && e.k !== TOGAWA_POR_DEFECTO.k ? e.k : undefined, v: num(e.v) && e.v !== TOGAWA_POR_DEFECTO.v ? e.v : undefined,
  });
  const trazas = [...t.trazas];
  if ((num(e.k) && e.k !== TOGAWA_POR_DEFECTO.k) || (num(e.v) && e.v !== TOGAWA_POR_DEFECTO.v)) {
    trazas[0] = { ...trazas[0], nota: `${trazas[0].nota}. Se cambió el valor por defecto (K = ${numEc(TOGAWA_POR_DEFECTO.k)}, V = ${numEc(TOGAWA_POR_DEFECTO.v)})` };
  }
  const r = rset({
    deteccionS: num(e.deteccionS) ? e.deteccionS : 0, alarmaS: num(e.alarmaS) ? e.alarmaS : 0,
    premovimientoS: num(e.premovimientoS) ? e.premovimientoS : undefined, togawaS: t.tsSegundos,
  });
  trazas.push(...r.trazas);
  return cerrar({ ...base, trazas, resumen: { n, tsSegundos: t.tsSegundos, tsMinutos: t.tsMinutos, k: t.k, v: t.v, rsetSegundos: r.rsetSegundos } });
}

// ── Punto de encuentro (PE-24) ──────────────────────────────────────────────

export function evaluarPunto(e: EntradaPuntoPaso3, c: ContextoPaso3, aforo: number | null): Evaluacion {
  const base = { clave: 'punto_encuentro', metodo: 'punto_encuentro' as const, sectorId: null, titulo: 'Punto de encuentro' };
  const ocupantes = aforo ?? c.trabajadores;
  const trazas: Traza[] = [];
  const sinResponder = CONDICIONES_PUNTO_ENCUENTRO.filter(x => e.condiciones[x.clave] === undefined);
  if (sinResponder.length) trazas.push(pv('punto_encuentro', 'Criterio técnico del responsable', 'Condiciones', `Faltan ${sinResponder.length} condiciones: ${sinResponder.map(x => x.etiqueta).join(', ')}`, 3));
  if (!num(ocupantes)) {
    trazas.push(pv('punto_encuentro', 'Criterio técnico del responsable', 'Capacidad', 'Faltan los trabajadores (capítulo 1) o el aforo', 1));
    return cerrar({ ...base, trazas, resumen: {} });
  }
  const r = evaluarPuntoEncuentro({
    ocupantes,
    m2PorPersona: num(e.m2PorPersona) ? e.m2PorPersona : undefined,
    areaDisponibleM2: num(e.areaDisponibleM2) ? e.areaDisponibleM2 : undefined,
    distanciaM: num(e.distanciaM) ? e.distanciaM : undefined,
    distanciaMinimaM: num(e.distanciaMinimaM) ? e.distanciaMinimaM : undefined,
    condiciones: e.condiciones,
    peligros: PELIGROS_PUNTO_ENCUENTRO.filter(p => e.peligros[p.clave]).map(p => ({
      peligro: p.etiqueta, probabilidad: e.peligros[p.clave]!.p, consecuencia: e.peligros[p.clave]!.c,
    })),
  });
  // PE-A3c: un punto "no apto" genera UNA sola acción (clave estable 'punto_encuentro|no_apto') que lista las
  // condiciones eliminatorias que fallan. Esas condiciones no generan acción propia, y la matriz P × C (moderado o
  // peor) solo genera acciones si el punto es apto: si no lo es, hay que cambiar de punto. Las acciones guardadas
  // que ya no se derivan quedan "origen resuelto" al guardar el paso 3 (sincronizarAcciones).
  const fallan = CONDICIONES_PUNTO_ENCUENTRO.filter(x => x.eliminatoria && e.condiciones[x.clave] === false).map(x => x.etiqueta);
  const PC = 'Probabilidad × consecuencia';
  trazas.push(...r.trazas.map(t => {
    if (t.estado === 'por_validar') return { ...t, paso: 3 };
    if (r.descartado && t.articulo_o_tabla === PC && t.estado === 'no_cumple') {
      return { ...t, nota: (t.nota ?? '').replace('; genera acción obligatoria en el plan de acción', '; sin acción propia mientras el punto no sea apto') };
    }
    return t;
  }));
  const extra: AccionDerivada[] = r.descartado ? [{
    sourceKey: 'punto_encuentro|no_apto', sourceMethod: 'punto_encuentro', sectorId: null,
    norma: 'Criterio técnico del responsable', articulo: 'Condiciones eliminatorias',
    descripcion: `El punto de encuentro NO es apto. ${fallan.length === 1 ? 'Falla la condición eliminatoria' : `Fallan ${fallan.length} condiciones eliminatorias`}: `
      + `${fallan.join('; ')}. Elegir otro punto o corregir la condición.`,
  }] : [];
  if (r.descartado) {
    trazas.unshift({
      metodo: 'punto_encuentro', norma: 'Criterio técnico del responsable', articulo_o_tabla: 'Resultado',
      umbral: 'todas las condiciones', valor: 'no apto', unidad: '—', estado: 'no_cumple', fuente_doc: doc.paso0('§7 PE-24'),
      nota: `No apto: falla ${fallan.join('; ')}; una sola acción obligatoria`,
    });
  }
  // PE-A4b: con una eliminatoria sin responder el punto no es "apto" sino "por validar" (vista previa y PDF).
  const eliminatoriasSinResponder = sinResponder.filter(x => x.eliminatoria).map(x => x.etiqueta);
  const ev = cerrar({ ...base, trazas, resumen: {
    ocupantes, descartado: r.descartado, eliminatoriasSinResponder, areaRequeridaM2: r.areaRequeridaM2, peligros: r.evaluacion,
  } }, extra);
  const eliminatorias = new Set<string>(CONDICIONES_PUNTO_ENCUENTRO.filter(x => x.eliminatoria).map(x => x.etiqueta));
  // Clave estable de P × C: siempre con el peligro (con un solo peligro, claveTraza la dejaría sin él y la acción
  // guardada quedaría huérfana al agregar un segundo peligro). Las acciones salen en el orden de sus trazas.
  const pcTrazas = ev.trazas.filter(t => t.articulo_o_tabla === PC && t.estado === 'no_cumple');
  let i = 0;
  const acciones = ev.acciones.map(a => (a.articulo === PC
    ? { ...a, sourceKey: `punto_encuentro|${PC}|${(pcTrazas[i++]?.nota ?? '').split(/[:(]/)[0].trim()}` } : a));
  return {
    ...ev,
    acciones: acciones.filter(a => a.sourceKey !== 'punto_encuentro|Resultado' && !eliminatorias.has(a.articulo)
      && !(r.descartado && a.articulo === PC)),
  };
}

// ── Verificación normativa (AM 1257; RTQ si el cantón es 1701) ──────────────

const OCUPACION_VERIFICACION = (o: Ocupacion | null): 'industrial' | 'almacenamiento' | 'oficinas' | 'otra' =>
  o === 'industrial' || o === 'almacenamiento' || o === 'oficinas' ? o : 'otra';

export function anosDesde(fechaIso: string, hoy: Date): number {
  const f = new Date(`${fechaIso}T00:00:00`);
  if (Number.isNaN(f.getTime())) throw new Error(`Fecha inválida: ${fechaIso}`);
  return (hoy.getTime() - f.getTime()) / (365.25 * 24 * 3600 * 1000);
}

export function evaluarVerificacion(e: EntradaVerificacionPaso3, c: ContextoPaso3, hoy: Date = new Date()): Evaluacion {
  const base = { clave: 'verificacion', metodo: 'verificacion' as const, sectorId: null, titulo: 'Verificación normativa' };
  if (!num(c.trabajadores)) {
    return cerrar({ ...base, trazas: [pv('verificacion', 'AM 1257', 'Art. 169', 'Faltan los trabajadores', 1)], resumen: {} });
  }
  const il = e.iluminacion ?? {};
  const ilum = [il.duracionMin, il.luxPromedio, il.luxMinimo, il.separacionMaxM].some(num) ? {
    duracionMin: num(il.duracionMin) ? il.duracionMin : undefined, luxPromedio: num(il.luxPromedio) ? il.luxPromedio : undefined,
    luxMinimo: num(il.luxMinimo) ? il.luxMinimo : undefined, separacionMaxM: num(il.separacionMaxM) ? il.separacionMaxM : undefined,
  } : undefined;
  const trazas = verificar({
    canton: esQuitoCodigo(c) ? 'Quito' : 'fuera del DMQ', ocupacion: OCUPACION_VERIFICACION(c.ocupacion), trabajadores: c.trabajadores,
    tieneBrigada: c.brigadaMiembros !== null ? c.brigadaMiembros > 0 : undefined,
    distanciaMaxExtintorM: num(e.distanciaMaxExtintorM) ? e.distanciaMaxExtintorM : undefined,
    iluminacion: ilum,
    hidrostatica: e.fechaHidrostatica ? {
      anosDesdeUltimaPrueba: anosDesde(e.fechaHidrostatica, hoy),
      intervaloAnos: num(e.intervaloHidrostaticaAnos) && e.intervaloHidrostaticaAnos !== 6 ? e.intervaloHidrostaticaAnos : undefined,
    } : undefined,
  });
  const faltan: Traza[] = [];
  if (!num(e.distanciaMaxExtintorM)) faltan.push(pv('verificacion', 'AM 1257', 'Art. 120', 'Falta la distancia máxima a un extintor', 3));
  if (!ilum) faltan.push(pv('verificacion', 'AM 1257', 'Art. 23', 'Faltan las mediciones de la iluminación de emergencia', 3));
  if (!e.fechaHidrostatica) faltan.push(pv('verificacion', 'AM 1257', 'Art. 32 d)', 'Falta la fecha de la última prueba hidrostática', 3));
  if (c.brigadaMiembros === null) faltan.push(pv('verificacion', 'AM 1257', 'Art. 169', 'No se pudo leer la brigada (emergency_brigade_members)', 3));
  return cerrar({ ...base, trazas: trazas.concat(brigadaCapacitadaTrazas(c, hoy), porSectorTrazas(e, c), faltan), resumen: { quito: esQuitoCodigo(c), brigadaMiembros: c.brigadaMiembros } });
}

// PE-38 (Kevin): la brigada cuenta como capacitada si cada brigadista tiene una capacitación registrada en los últimos
// 12 meses (emergency_brigade_members.trained y training_date). Sin fecha → por validar. Sin brigada, no hay traza.
export const ARTICULO_PE38 = 'Brigada capacitada';
function brigadaCapacitadaTrazas(c: ContextoPaso3, hoy: Date): Traza[] {
  if (!c.brigada || c.brigada.length === 0) return [];
  const r = capacitacionBrigada(c.brigada, hoy);
  const estado: Traza['estado'] = r.estado === 'capacitada' ? 'cumple' : r.estado === 'no_capacitada' ? 'no_cumple' : 'por_validar';
  return [{
    metodo: 'verificacion', norma: 'Criterio técnico de StorSafe (por validar)', articulo_o_tabla: ARTICULO_PE38,
    umbral: 'cada brigadista con una capacitación en los últimos 12 meses', valor: `${r.vigentes} de ${r.personas}`, unidad: 'personas',
    estado, fuente_doc: FUENTE_PUBLICA.criterio, // interno: CONTEXTO_STORSAFE.md §17 ítem 163 (PE-38)
    nota: r.estado === 'capacitada' ? 'Toda la brigada tiene una capacitación en los últimos 12 meses'
      : r.estado === 'no_capacitada' ? `${plural(r.vencidasONo, 'brigadista', 'brigadistas')} sin capacitación en los últimos 12 meses`
      : `Falta la fecha de capacitación de ${plural(r.sinFecha, 'brigadista', 'brigadistas')}: se completa en Plan de Emergencias → Brigada de Emergencia`,
  }];
}

// Por sector (H2 y T10). Extintor: 20 m (AM 1257, Art. 120) o 15 m en Quito (RTQ 7, 15.4): gana la más exigente.
// Recorrido (PE-A3c2): ver recorridoTrazas.
function porSectorTrazas(e: EntradaVerificacionPaso3, c: ContextoPaso3): Traza[] {
  const quito = esQuitoCodigo(c);
  const regla = quito ? EXTINTOR_DISTANCIA_QUITO : EXTINTOR_DISTANCIA_NACIONAL;
  const out: Traza[] = [];
  for (const s of c.sectores) {
    const v = s.sector.id ? e.porSector?.[s.sector.id] : undefined;
    if (!v) continue;
    if (num(v.distanciaExtintorM)) {
      out.push({
        metodo: 'verificacion', norma: regla.norma, articulo_o_tabla: `${regla.articulo_o_tabla} · ${s.sector.name}`, umbral: regla.valor,
        valor: v.distanciaExtintorM, unidad: 'm', estado: v.distanciaExtintorM <= regla.valor ? 'cumple' : 'no_cumple', fuente_doc: regla.fuente_doc,
        nota: `Distancia máxima a un extintor en el sector${quito ? ': en Quito, 15 m (RTQ 7) es más exigente que 20 m (AM 1257 Art. 120)' : ''}`,
      });
    }
    if (num(v.recorridoM)) out.push(...recorridoTrazas(s.sector, v, v.recorridoM, quito));
  }
  return out;
}

// Recorrido máximo de evacuación del sector (PE-A3c2, caso T10). Clave de origen: "verificacion|Recorrido · <sector>".
// - Rociadores: emergency_plan_sectors.has_sprinklers (M33; Sí/No, por defecto No: el lado más exigente). Si el sector
//   no trae la columna, se usa lo que hubiera en porSector (respaldo de PE-A3c2).
// - Fuera de Quito: AM 1257 Art. 9, 25 m ("puede variar en función del tipo de edificación y grado de riesgo"); los
//   rociadores no cambian ese límite.
// - En Quito (canton_code 1701), PE-41 (JC y Claude, 28/10/2026): se aplica la RTQ 3, 6.3 (b) si la actividad del
//   sector es almacenamiento y 11.2 (b) si es industrial, según el nivel de riesgo (sin él, por validar) y los
//   rociadores; la traza del Art. 9 queda informativa. Otras actividades: la RTQ 3 de esa ocupación no está cargada →
//   por validar (se informa cómo queda frente a los 25 m nacionales).
export const NOTA_PE41 = 'En Quito se aplica la RTQ 3; el AM 1257 Art. 9 permite variar los 25 m según el tipo de edificación y el grado de riesgo (por validar)';
export const rociadoresDe = (sector: SectorEnPantalla['sector'], v?: EntradaSectorVerificacion): boolean =>
  typeof sector.has_sprinklers === 'boolean' ? sector.has_sprinklers : v?.rociadores === true;

function recorridoTrazas(sector: SectorEnPantalla['sector'], v: EntradaSectorVerificacion, recorridoM: number, quito: boolean): Traza[] {
  const articulo = `Recorrido · ${sector.name}`;
  const base = { metodo: 'verificacion', articulo_o_tabla: articulo, valor: recorridoM, unidad: 'm' } as const;
  const pend = (norma: string, nota: string, fuente: string): Traza => ({ ...base, norma, umbral: null, estado: 'por_validar', fuente_doc: fuente, paso: 3, nota });
  const rociadores = rociadoresDe(sector, v);
  const nac = RECORRIDO_NACIONAL;
  const frenteNacional = `${recorridoM <= nac.valor ? 'cumple' : 'no cumple'} los ${nac.valor} m del AM 1257 Art. 9`;
  if (!quito) {
    return [{
      ...base, norma: nac.norma, umbral: nac.valor, estado: recorridoM <= nac.valor ? 'cumple' : 'no_cumple', fuente_doc: nac.fuente_doc,
      nota: `AM 1257, Art. 9: máximo ${nac.valor} m ("puede variar en función del tipo de edificación y grado de riesgo"); `
        + `los rociadores${rociadores ? '' : ' (no tiene)'} no cambian el límite nacional`,
    }];
  }
  const occ = sector.occupancy === 'almacenamiento' || sector.occupancy === 'industrial' ? sector.occupancy : null;
  if (!occ) {
    return [pend('RTQ 3/2026', `En Quito, el límite de recorrido de la RTQ 3 para la actividad "${sector.occupancy}" no está cargado `
      + `(solo almacenamiento, 6.3 b, e industrial, 11.2 b); el recorrido ${frenteNacional}`, `${doc.matriz('39, 47, 49')}`)];
  }
  const regla = RECORRIDO_RTQ3[occ];
  if (!v.riesgoRtq3) return [pend('RTQ 3/2026', `Falta el nivel de riesgo según la RTQ 3 (${regla.articulo})`, regla.fuente_doc)];
  const limite = regla.limites[v.riesgoRtq3][rociadores ? 1 : 0];
  const nivel = NIVELES_RIESGO_RTQ3.find(n => n.valor === v.riesgoRtq3)!.etiqueta.toLowerCase();
  const sinAmpliar = occ === 'industrial' && v.riesgoRtq3 === 'alto' && rociadores ? '; en riesgo alto la RTQ 3 no amplía el límite con rociadores' : '';
  return [{
    ...base, norma: 'RTQ 3/2026', umbral: limite, estado: recorridoM <= limite ? 'cumple' : 'no_cumple', fuente_doc: regla.fuente_doc,
    nota: `RTQ 3, ${regla.articulo}: ${occ}, ${nivel}, ${rociadores ? 'con' : 'sin'} rociadores automáticos → máximo ${limite} m${sinAmpliar}`,
  }, {
    metodo: 'verificacion', norma: nac.norma, articulo_o_tabla: `Recorrido (AM 1257 Art. 9) · ${sector.name}`, umbral: nac.valor, valor: recorridoM,
    unidad: 'm', estado: 'informativo', fuente_doc: nac.fuente_doc, nota: NOTA_PE41,
  }];
}

// ── Amenaza × vulnerabilidad, por amenaza ────────────────────────────────────

export function evaluarAmenazas(lista: EntradaAmenazaPaso3[], c: ContextoPaso3, verificacionTrazas: Traza[]): Evaluacion {
  const base = { clave: 'amenaza_vulnerabilidad', metodo: 'amenaza_vulnerabilidad' as const, sectorId: null, titulo: 'Amenazas y vulnerabilidad' };
  const trazas: Traza[] = [];
  const extra: AccionDerivada[] = [];
  const resumen: Record<string, unknown> = {};
  for (const a of lista) {
    let vuln;
    try {
      vuln = vulnerabilidadPorAmenaza({ amenaza: a.tipo, nombre: a.nombre, respuestas: a.respuestas, verificacion: verificacionTrazas, ajuste: a.ajuste ?? undefined });
    } catch (err) {
      trazas.push(pv('amenaza_vulnerabilidad', 'Criterios de preparación de StorSafe (por validar)', `Vulnerabilidad: ${a.nombre}`, err instanceof Error ? err.message : String(err), 3));
      continue;
    }
    let nivelAmenaza;
    if (a.tipo === 'sismo') {
      if (!c.zona) { trazas.push(pv('amenaza_vulnerabilidad', 'NEC-SE-DS', `Amenaza: ${a.nombre}`, 'Falta la zona sísmica', 1)); continue; }
      nivelAmenaza = { tipo: 'sismo' as const, zonaSismica: c.zona };
    } else {
      if (!a.nivel) { trazas.push(pv('amenaza_vulnerabilidad', 'Fuente del responsable', `Amenaza: ${a.nombre}`, 'Falta el nivel de la amenaza', 3)); continue; }
      if (!a.fuente || !a.fuente.trim()) {
        trazas.push(pv('amenaza_vulnerabilidad', 'Fuente del responsable', `Amenaza: ${a.nombre}`, 'Falta la fuente (mapa del IGEPN o PDOT del GAD): sin fuente no se guarda', 3));
        continue;
      }
      nivelAmenaza = { tipo: a.tipo as 'volcan' | 'inundacion' | 'otra', nivel: a.nivel, fuente: a.fuente };
    }
    const r = evaluarAmenaza({ amenaza: a.nombre, nivelAmenaza, vulnerabilidad: { modo: 'criterios', resultado: vuln } });
    trazas.push(...r.trazas.map(t => ({ ...t, articulo_o_tabla: t.articulo_o_tabla.includes(a.nombre) ? t.articulo_o_tabla : `${a.nombre} · ${t.articulo_o_tabla}` })));
    resumen[a.id] = { nombre: a.nombre, amenaza: r.nivelAmenaza, vulnerabilidad: r.vulnerabilidad, riesgo: r.riesgo, porcentaje: vuln.porcentaje };
    if (r.vulnerabilidad === 'alto' || r.vulnerabilidad === 'muy_alto') {
      extra.push({
        sourceKey: `amenaza_vulnerabilidad|vulnerabilidad|${a.id}`, sourceMethod: 'amenaza_vulnerabilidad', sectorId: null,
        norma: 'Criterios de preparación de StorSafe (por validar)', articulo: `Vulnerabilidad: ${a.nombre}`,
        descripcion: `Bajar la vulnerabilidad ante "${a.nombre}" (hoy ${r.vulnerabilidad === 'alto' ? 'alta' : 'muy alta'}, ${redondear(vuln.porcentaje, 1).toString().replace('.', ',')} %): cumplir los criterios de preparación que fallan.`,
      });
    }
  }
  return cerrar({ ...base, trazas, resumen }, extra);
}

export const amenazaDeZona = (z: ZonaSismica | null) => (z ? amenazaSismica(z) : null);
export { criteriosDe };

// ── Todo el paso 3 ───────────────────────────────────────────────────────────

export function calcularPaso3(e: EntradasPaso3, c: ContextoPaso3, hoy: Date = new Date()): Evaluacion[] {
  const meseri = unidadesMeseri(c.sectores).map(u => evaluarMeseri(u, e.meseri[u.clave], c));
  const aforo = evaluarAforo(e.aforo, c);
  const verificacion = evaluarVerificacion(e.verificacion, c, hoy);
  return [
    ...meseri,
    aforo,
    evaluarEvacuacion(e.evacuacion, c, aforoDe(aforo)),
    evaluarPunto(e.punto, c, aforoDe(aforo)),
    verificacion,
    evaluarAmenazas(e.amenazas, c, verificacion.trazas),
  ];
}

// ── Firma del resultado: para avisar si cambió desde que se guardó ──────────

export function firmaResultado(trazas: Traza[]): string {
  return JSON.stringify(trazas.map(t => [CLAVE_ARTICULO_LEGADA[t.articulo_o_tabla] ?? t.articulo_o_tabla, t.estado, typeof t.valor === 'number' ? redondear(t.valor, 2) : t.valor]));
}

export const ESCALA_P = ESCALA_MESERI;
